import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Badge, Button, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { collectUnfinished, localDateKey, markItemDone, moveToTomorrow, writeClose, type UnfinishedItem } from "../lib/dayClose";
import { notifyDataChanged } from "../hooks/useCourses";

/**
 * Midnight summary: once a new day begins and yesterday wasn't closed yet,
 * show the day's efficiency, offer moving unfinished items to today, then
 * freeze the record (past days are view-only).
 */
export function DayClosePopup() {
  const { t, formatDate, formatNumber } = useI18n();
  const [state, setState] = useState<{ dayKey: string; total: number; done: number; items: UnfinishedItem[] } | null>(null);
  const [open, setOpen] = useState(false);
  const [moved, setMoved] = useState<Set<string>>(new Set());
  const [doneNow, setDoneNow] = useState<Set<string>>(new Set());

  useEffect(() => {
    // yesterday's key; closes are stored locally per device
    const check = async () => {
      const now = new Date();
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const dayKey = localDateKey(yesterday);
      const storageKey = "nelurio.dayclose.seen." + dayKey;
      if (window.localStorage.getItem(storageKey)) return;
      const result = await collectUnfinished(dayKey);
      // Mark the day seen ONLY when the data is trustworthy — a failed fetch
      // (ok=false) used to permanently suppress the recap for a non-empty day.
      if (!result.ok) return;
      // Only show when there was anything at all that day.
      if (result.total === 0 && result.items.length === 0) {
        window.localStorage.setItem(storageKey, "1");
        return;
      }
      setState({ dayKey, ...result });
      setOpen(true);
    };
    const timer = window.setTimeout(() => void check(), 1800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      // Escape = postpone only: the recap returns next launch until the day
      // is actually banked with «بستن روز» — the seen flag is NOT set here.
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open || !state) return null;

  const efficiency = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;

  const finish = () => {
    writeClose({
      date: state.dayKey,
      total: state.total,
      done: state.done,
      efficiency,
      moved: moved.size,
      closedAt: new Date().toISOString()
    });
    window.localStorage.setItem("nelurio.dayclose.seen." + state.dayKey, "1");
    setOpen(false);
  };

  const moveItem = async (item: UnfinishedItem) => {
    setMoved((current) => new Set(current).add(item.id));
    try {
      await moveToTomorrow(item, state.dayKey);
      notifyDataChanged();
    } catch {
      // Roll back the green badge — finish() used to bank a move that never happened.
      setMoved((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
    }
  };

  // Retro-active tick: the task WAS done that day, the checkbox was forgotten.
  // Counts into the day's efficiency; toggling back removes it again.
  const toggleDidIt = async (item: UnfinishedItem) => {
    const nowDone = !doneNow.has(item.id);
    setDoneNow((current) => {
      const next = new Set(current);
      if (nowDone) next.add(item.id); else next.delete(item.id);
      return next;
    });
    setState((current) => current && { ...current, done: Math.max(0, Math.min(current.total, current.done + (nowDone ? 1 : -1))) });
    await markItemDone(item, state.dayKey, nowDone).catch(() => {
      setDoneNow((current) => {
        const next = new Set(current);
        if (nowDone) next.delete(item.id); else next.add(item.id);
        return next;
      });
      setState((c) => c && { ...c, done: Math.max(0, Math.min(c.total, c.done + (nowDone ? -1 : 1))) });
    });
    notifyDataChanged();
  };

  // Session-only dismiss: the popup was jailing every dropdown beneath its
  // full-screen backdrop (owner report: Khoze settings + forest selects dead).
  const postpone = () => setOpen(false);

  return createPortal(
    <div className="composer-backdrop" role="presentation" onClick={(event) => { if (event.target === event.currentTarget) postpone(); }}>
      <div className="composer-modal dayclose rise" role="dialog" aria-modal="true">
        <div className="composer-modal__head">
          <h2>{t("dayclose.title")}</h2>
          <button className="composer-modal__close" onClick={postpone} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
        </div>
        <p className="dayclose__sub">
          {formatDate(`${state.dayKey}T12:00:00`, { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <div className="dayclose__score" style={{ "--progress": `${efficiency * 3.6}deg` } as React.CSSProperties}>
          <span className="dayclose__score-ticks" aria-hidden="true" />
          <span className="dayclose__score-glint" aria-hidden="true" />
          <div className="dayclose__score-center">
            <strong>{formatNumber(efficiency)}<i>%</i></strong>
            <small>{t("dayclose.efficiency")}</small>
          </div>
        </div>
        <div className="dayclose__facts">
          <div><span>{t("dayclose.total")}</span><strong>{formatNumber(state.total)}</strong></div>
          <div><span>{t("dayclose.done")}</span><strong>{formatNumber(state.done)}</strong></div>
          <div><span>{t("dayclose.unfinished")}</span><strong>{formatNumber(state.items.length - moved.size - doneNow.size)}</strong></div>
        </div>

        {state.items.length > 0 && (
          <div className="dayclose__list">
            <div className="dayclose__list-title"><strong>{t("dayclose.unfinishedTitle")}</strong><Badge tone="amber">{formatNumber(state.items.length - moved.size - doneNow.size)}</Badge></div>
            <p className="dayclose__hint">{t("dayclose.didItHint")}</p>
            {state.items.map((item) => (
              <article className="dayclose__item" key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <small>{t(item.kind === "task" ? "dayclose.kindTask" : item.kind === "routine" ? "routine.title" : "reviews.title")}{item.time ? ` · ${item.time}` : ""}</small>
                </div>
                {moved.has(item.id)
                  ? <Badge tone="green">{t("dayclose.moved")}</Badge>
                  : doneNow.has(item.id)
                    ? <Badge tone="green">{t("dayclose.doneNow")}</Badge>
                    : (
                      <div className="dayclose__actions">
                        <Button variant="secondary" icon="check" onClick={() => void toggleDidIt(item)}>{t("dayclose.didIt")}</Button>
                        {item.kind === "task"
                          ? <Button variant="secondary" icon="arrow" onClick={() => void moveItem(item)}>{t("dayclose.moveToTomorrow")}</Button>
                          : <Badge tone="neutral">{t("dayclose.repeats")}</Badge>}
                      </div>
                    )}
              </article>
            ))}
          </div>
        )}

        <p className="tree-picker__hint">{t("dayclose.lockNote")}</p>
        <div className="composer-modal__actions">
          <Button icon="check" onClick={finish}>{t("dayclose.closeDay")}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
